import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';

export interface LocationTaxBreakdown {
  deliveryLocationId: string;
  locationName: string;
  state: string;
  taxableAmountCents: number;
  taxAmountCents: number;
  taxRatePercentage: number;
  isExempt: boolean;
  exemptionType?: string;
}

export interface TaxPreviewResult {
  totalTaxCents: number;
  totalTaxableAmountCents?: number;
  calculationId?: string | null;
  locationBreakdowns: LocationTaxBreakdown[];
  taxEnabled: boolean;
  hasExemptions?: boolean;
  message?: string;
  correlationId?: string;
}

export function useTaxPreview(jobId?: string) {
  return useQuery({
    queryKey: ['tax-preview', jobId],
    queryFn: async (): Promise<TaxPreviewResult> => {
      if (!jobId) {
        return {
          totalTaxCents: 0,
          totalTaxableAmountCents: 0,
          locationBreakdowns: [],
          taxEnabled: false,
          hasExemptions: false,
        };
      }

      const { data, error } = await invokeEdgeFunction('calculate-tax-preview', {
        body: { jobId },
        callerOperation: 'useTaxPreview.calculate',
      });

      if (error) throw error;
      
      return data as TaxPreviewResult;
    },
    enabled: !!jobId,
    staleTime: 60000, // Cache for 1 minute
    retry: false, // Don't retry on failure (tax preview is optional)
  });
}

// Hook to fetch tax details for an existing transaction
export function useTransactionTaxDetails(transactionId?: string) {
  return useQuery({
    queryKey: ['transaction-tax-details', transactionId],
    queryFn: async () => {
      if (!transactionId) return null;

      const { data, error } = await (supabase as any)
        .from('payment_transaction_tax_details')
        .select(`
          *,
          delivery_location:delivery_location_id (
            delivery_name,
            state_province,
            city
          ),
          exemption_certificate:exemption_certificate_id (
            exemption_type,
            state_code
          )
        `)
        .eq('payment_transaction_id', transactionId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!transactionId,
  });
}
